import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { getChatControlData } from 'components/ChatDialog/store/slices/chatSlice';
import { PROJECT_EMAIL_READ } from 'scenes/Permissions/constants/permissions';
import { selectIsRequiredPermissionForProject } from 'scenes/Permissions/store/selectors/personalPermissionsSelectors';
import { getProjectEmailData } from 'store/projectEmail/projectEmailSlice';
import { selectProjects } from 'store/projects/selectors/projectsSelectors';
import {
  selectCurrentUserEmployerProjects,
  selectCurrentUserResourcesFromActiveEmployerIndexedByProject,
} from 'store/resources/selectors/resources.selectors';
import { selectUserActiveEmployerId } from 'store/user/selectors.js/user.selectors';
import isFeatureEnabled, { Feature } from 'utils/isFeatureEnabled';
import { countWaitingRows, unreadWindowCutoff } from './messageCenterRows';

const MY_LIXANI = 'myLixani';

/**
 * The projects the message center may show, scoped to the active employer.
 *
 * Scoping only means anything while the active employer is one of the user's
 * own: MyLixani and an id left over from an employer the user has since left
 * match no project at all, and filtering on one emptied the drawer outright
 * while the badges kept counting. The relation is the one the projects list
 * uses — a resource in the project employed by the active employer — because
 * masterParent alone is the top of the project hierarchy, which is not the
 * employer whenever the work is subcontracted.
 */
export const useMessageCenterProjects = () => {
  const allProjects = useSelector(selectProjects);
  const activeEmployerId = useSelector(selectUserActiveEmployerId);
  const employerProjects = useSelector(selectCurrentUserEmployerProjects);
  const projectIdsOfActiveEmployer = useSelector(
    selectCurrentUserResourcesFromActiveEmployerIndexedByProject
  );

  return React.useMemo(() => {
    const scopes =
      Boolean(activeEmployerId) &&
      (activeEmployerId === MY_LIXANI ||
        employerProjects.some((project) => project.id === activeEmployerId));
    if (!scopes) return allProjects;
    return Object.fromEntries(
      Object.entries(allProjects).filter(
        ([projectId, project]) =>
          project.id === activeEmployerId ||
          project.masterParent === activeEmployerId ||
          Boolean(projectIdsOfActiveEmployer[projectId])
      )
    );
  }, [
    allProjects,
    activeEmployerId,
    employerProjects,
    projectIdsOfActiveEmployer,
  ]);
};

/**
 * Project email read permission per email-enabled project, plus the employer
 * projects that get an email row of their own.
 *
 * The permission check walks the project ancestry and the user's resources per
 * project, and outside production every project has email, so building the map
 * for all of them is not free — and the badges below hold this open for the
 * whole session. `needsPermission` narrows it to the projects that could put a
 * row on screen at all; the drawer passes nothing and gets the full map, which
 * its search needs. Shallow-compared so a rebuild that changed nothing does not
 * re-render the app bar.
 */
export const useProjectEmailRows = (projects, needsPermission) => {
  const emailEnabledProjects = React.useMemo(
    () =>
      Object.values(projects).filter((project) =>
        isFeatureEnabled(Feature.PROJECT_EMAIL, project)
      ),
    [projects]
  );

  const projectsToCheck = React.useMemo(
    () =>
      needsPermission
        ? emailEnabledProjects.filter(needsPermission)
        : emailEnabledProjects,
    [emailEnabledProjects, needsPermission]
  );

  const projectEmailPermissions = useSelector((state) => {
    const permissions = {};
    projectsToCheck.forEach((project) => {
      permissions[project.id] = selectIsRequiredPermissionForProject(state, {
        projectId: project.id,
        permission: PROJECT_EMAIL_READ,
      });
    });
    return permissions;
  }, shallowEqual);

  const employerProjectsWithEmail = React.useMemo(
    () =>
      projectsToCheck.filter(
        (project) =>
          project.type === 'EMPLOYER' && projectEmailPermissions[project.id]
      ),
    [projectsToCheck, projectEmailPermissions]
  );

  return { projectEmailPermissions, employerProjectsWithEmail };
};

/** The unread sources the row tests read, in the shape they expect. */
export const useMessageCenterSources = (projectEmailPermissions) => {
  const { unreadMessages, emailLastMessageAt = {} } =
    useSelector(getProjectEmailData);

  return React.useMemo(
    () => ({ projectEmailPermissions, unreadMessages, emailLastMessageAt }),
    [projectEmailPermissions, unreadMessages, emailLastMessageAt]
  );
};

/** The number of rows the message center would list right now. */
export const useMessageCenterUnreadCount = () => {
  const { threads } = useSelector(getChatControlData);
  const projects = useMessageCenterProjects();
  const { unreadMessages, emailLastMessageAt = {} } =
    useSelector(getProjectEmailData);

  // An email permission only decides anything where email is actually waiting,
  // so the count never asks about the rest.
  const hasUnreadEmail = React.useCallback(
    (project) => unreadMessages[project.projectKey] > 0,
    [unreadMessages]
  );
  const { projectEmailPermissions, employerProjectsWithEmail } =
    useProjectEmailRows(projects, hasUnreadEmail);

  return React.useMemo(
    () =>
      countWaitingRows({
        threads,
        projects,
        employerProjectsWithEmail,
        sources: {
          projectEmailPermissions,
          unreadMessages,
          emailLastMessageAt,
        },
        cutoff: unreadWindowCutoff(),
      }),
    [
      threads,
      projects,
      employerProjectsWithEmail,
      projectEmailPermissions,
      unreadMessages,
      emailLastMessageAt,
    ]
  );
};
